/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
// Load list plugin dynamically so app still builds if it's not installed
import axiosClient from '../../api/axiosClient';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';

const BRAND_GOLD = '#7d6633';

const STATUS_COLORS = {
  confirmed: BRAND_GOLD,
  pending: '#F59E0B',
  completed: '#10B981',
  cancelled: '#6B7280',
  canceled: '#6B7280',
  no_show: '#EF4444'
};

function formatISODate(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function mapBookingToEvent(booking, t) {
  const status = String(booking.status || '').toLowerCase();
  const color = STATUS_COLORS[status] || BRAND_GOLD;

  const start = booking.start_local || booking.start_time || booking.start_utc || booking.start || booking.startAt;
  const end = booking.end_local || booking.end_time || booking.end_utc || booking.end || booking.endAt;

  const client = booking.client?.name || booking.client_name || booking.client?.email || t('admin.col_client');
  const service = booking.service?.name || booking.service_name || booking.Service?.name || booking.name || t('admin.col_service');
  const barber = booking.barber?.name || booking.barber_name || '';

  return {
    id: booking.id,
    title: client,
    start,
    end,
    // Keep container transparent; we render a styled chip inside
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    classNames: ['hc-event'],
    extendedProps: {
      rawBooking: booking,
      status,
      service,
      barber,
      chipColor: color,
    }
  };
}

export default function BookingsCalendar({ mode = 'admin', initialView = 'timeGridWeek' }) {
  const { user, shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const isBarber = user?.role === 'barber';
  const navigate = useNavigate();

  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [listPluginModule, setListPluginModule] = useState(null);
  const [isPhone, setIsPhone] = useState(false);
  const [isCompact, setIsCompact] = useState(false); // phone + tablets
  const [loading, setLoading] = useState(false);
  const [barbers, setBarbers] = useState([]);
  const [visibleFrom, setVisibleFrom] = useState('');
  const [visibleTo, setVisibleTo] = useState('');
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [selectedDate, setSelectedDate] = useState('');
  const [filters, setFilters] = useState({
    barber_id: isBarber ? user?.id : '',
    status: ''
  });

  const calendarApi = () => calendarRef.current?.getApi?.();

  const loadBarbers = useCallback(async () => {
    if (isBarber) return;
    try {
      const params = new URLSearchParams({ page: '1', pageSize: '200', status: 'active' });
      const res = await axiosClient.get(`/admin/barbers?${params}`);
      if (res.data?.ok) setBarbers(res.data.data || []);
    } catch (e) {
      // ignore
    }
  }, [isBarber, shopId]);

  const loadEvents = useCallback(async () => {
    if (!visibleFrom || !visibleTo) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        from: visibleFrom,
        to: visibleTo,
        page: '1',
        pageSize: '1000'
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.barber_id) params.append('barber_id', String(filters.barber_id));

      const res = await axiosClient.get(`/admin/bookings?${params.toString()}`);
      const items = res.data?.items || res.data || [];
      setEvents(items.map((item) => mapBookingToEvent(item, t)));
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filters, shopId, visibleFrom, visibleTo, t]);

  useEffect(() => {
    loadBarbers();
  }, [loadBarbers]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Dynamically import list plugin (optional dependency)
  useEffect(() => {
    (async () => {
      try {
        const mod = await import(/* webpackChunkName: "fc-list" */ '@fullcalendar/list');
        setListPluginModule(mod.default || mod);
      } catch (e) {
        setListPluginModule(null); // keep running without list view
      }
    })();
  }, []);

  // Track breakpoints and adapt UI.
  useEffect(() => {
    const phoneMql = window.matchMedia?.('(max-width: 640px)');
    const compactMql = window.matchMedia?.('(max-width: 1024px)');

    const apply = () => {
      setIsPhone(Boolean(phoneMql?.matches));
      setIsCompact(Boolean(compactMql?.matches));
    };

    apply();

    if (phoneMql?.addEventListener) phoneMql.addEventListener('change', apply);
    if (compactMql?.addEventListener) compactMql.addEventListener('change', apply);

    return () => {
      if (phoneMql?.removeEventListener) phoneMql.removeEventListener('change', apply);
      if (compactMql?.removeEventListener) compactMql.removeEventListener('change', apply);
    };
  }, []);

  const onDatesSet = useCallback((arg) => {
    setVisibleFrom(formatISODate(arg.start));
    setVisibleTo(formatISODate(arg.end));
    setCurrentTitle(arg.view?.title || '');
    setCurrentView(arg.view?.type || '');

    // Auto-scroll to current time when viewing today in timeGrid views
    try {
      const api = calendarRef.current?.getApi?.();
      const isTimeGrid = arg.view?.type?.startsWith('timeGrid');
      const viewedDate = new Date(arg.start);
      const today = new Date();
      const isToday = viewedDate.getFullYear() === today.getFullYear() &&
        viewedDate.getMonth() === today.getMonth() &&
        viewedDate.getDate() === today.getDate();

      if (api && isTimeGrid && isToday) {
        // Find the lane for the current hour and scroll it into view
        setTimeout(() => {
          const hour = String(today.getHours()).padStart(2, '0') + ':00:00';
          const scroller = api.el?.querySelector?.('.fc-timegrid-body .fc-scroller');
          const lane = api.el?.querySelector?.(`.fc-timegrid-slot-lane[data-time="${hour}"]`);
          if (scroller && lane) {
            const targetTop = lane.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
            scroller.scrollTo({ top: Math.max(targetTop - 48, 0), behavior: 'smooth' });
          }
        }, 50);
      }
    } catch (_) { /* noop */ }
  }, []);

  const getWeekView = useCallback(() => {
    // Week should feel like Android "agenda" everywhere when possible.
    if (listPluginModule) return 'listWeek';
    return 'timeGridWeek';
  }, [listPluginModule]);

  const getDayView = useCallback(() => {
    if (isCompact && listPluginModule) return 'listDay';
    return 'timeGridDay';
  }, [isCompact, listPluginModule]);

  // Note: we don't force-switch away from list views on desktop, because desktop Week now
  // intentionally uses the agenda-style list for consistency.

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const getBookingDayString = (event) => {
    const raw = event?.extendedProps?.rawBooking;
    const startStr =
      raw?.start_local ||
      raw?.start_time ||
      raw?.start_utc ||
      raw?.start ||
      raw?.startAt ||
      (typeof event?.startStr === 'string' ? event.startStr : '');

    if (typeof startStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(startStr)) {
      return startStr.slice(0, 10);
    }

    if (event?.start instanceof Date && !Number.isNaN(event.start.getTime())) {
      return formatISODate(event.start);
    }

    return '';
  };

  const handleEventClick = useCallback((clickInfo) => {
    // Direct navigation to booking management page, always scoping date range to the booking's day
    // so it will be visible even if it's next week/month.
    clickInfo?.jsEvent?.preventDefault?.();
    const id = clickInfo?.event?.id;
    if (!id) return;

    const day = getBookingDayString(clickInfo.event);
    const qp = new URLSearchParams();
    qp.set('focus_booking_id', String(id));
    if (day) {
      qp.set('from', day);
      qp.set('to', day);
    }
    // Ensure the focused booking is on the first payload even on busy days.
    qp.set('page', '1');
    qp.set('pageSize', '100');
    navigate(`/admin/bookings?${qp.toString()}`);
  }, [navigate]);

  const handleDateClick = useCallback((arg) => {
    const api = calendarApi();
    if (!api) return;
    const day = formatISODate(arg.date);
    setSelectedDate(day);

    // Android-like: tap a day in month view → open day agenda (phones + tablets).
    if (isCompact && api.view?.type === 'dayGridMonth') {
      const dayView = getDayView();
      api.gotoDate(arg.date);
      api.changeView(dayView);
    }
  }, [getDayView, isCompact]);

  const renderEventContent = (eventInfo) => {
    const status = eventInfo.event.extendedProps.status;
    const service = eventInfo.event.extendedProps.service;
    const barber = eventInfo.event.extendedProps.barber;
    const color = eventInfo.event.extendedProps.chipColor || STATUS_COLORS[status] || BRAND_GOLD;
    const viewType = eventInfo.view?.type || currentView;

    // Month grid: compact Android-like bars (no time text).
    if (viewType?.startsWith('dayGrid')) {
      return (
        <div
          className="hc-event-inner text-[10px] leading-tight"
          style={{
            borderLeft: `3px solid ${color}`,
            background: `${color}18`,
            borderRadius: 7,
            padding: '2px 4px'
          }}
          title={`${eventInfo.event.title}${service ? ` • ${service}` : ''}${barber ? ` • ${barber}` : ''}`}
        >
          <div className="font-semibold truncate">{eventInfo.event.title}</div>
        </div>
      );
    }

    return (
      <div className="hc-event-inner text-xs leading-tight" style={{ borderLeft: `4px solid ${color}`, background: `${color}14`, borderRadius: 8, padding: '4px 6px' }}>
        <div className="font-semibold mb-0.5 truncate">{eventInfo.timeText} · {eventInfo.event.title}</div>
        <div className="opacity-80 truncate">{service}{barber ? ` • ${barber}` : ''}</div>
      </div>
    );
  };

  return (
    <div className="w-full hc-calendar">
      {/* Unified toolbar (desktop + tablet + mobile) */}
      <div className="hc-cal-toolbar mb-2 flex flex-col gap-2">
        {/* On phones: make only the controls sticky (not the date/title). */}
        {isPhone ? (
          <>
            <div className="hc-cal-sticky flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={() => calendarApi()?.prev()}
                    className="hc-cal-btn px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-200"
                    aria-label={t('admin.previous')}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => calendarApi()?.next()}
                    className="hc-cal-btn px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-200"
                    aria-label={t('admin.next')}
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    onClick={() => calendarApi()?.today()}
                    className="hc-cal-btn px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-200"
                  >
                    {t('admin.today')}
                  </button>
                </div>
              </div>

              <div className="hc-cal-seg flex gap-2 overflow-x-auto -mx-1 px-1">
                <button
                  type="button"
                  onClick={() => calendarApi()?.changeView('dayGridMonth')}
                  className={`hc-cal-seg-btn px-3 py-2 text-sm rounded-xl border ${currentView === 'dayGridMonth' ? 'bg-[--accent] border-[--accent] text-white' : 'border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 text-zinc-700 dark:text-zinc-200'} backdrop-blur-xl transition`}
                  style={{ '--accent': BRAND_GOLD }}
                >
                  {t('admin.month')}
                </button>
                <button
                  type="button"
                  onClick={() => calendarApi()?.changeView(getWeekView())}
                  className={`hc-cal-seg-btn px-3 py-2 text-sm rounded-xl border ${currentView === getWeekView() ? 'bg-[--accent] border-[--accent] text-white' : 'border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 text-zinc-700 dark:text-zinc-200'} backdrop-blur-xl transition`}
                  style={{ '--accent': BRAND_GOLD }}
                >
                  {t('admin.week')}
                </button>
                <button
                  type="button"
                  onClick={() => calendarApi()?.changeView(getDayView())}
                  className={`hc-cal-seg-btn px-3 py-2 text-sm rounded-xl border ${currentView === getDayView() ? 'bg-[--accent] border-[--accent] text-white' : 'border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 text-zinc-700 dark:text-zinc-200'} backdrop-blur-xl transition`}
                  style={{ '--accent': BRAND_GOLD }}
                >
                  {t('admin.day')}
                </button>
              </div>
            </div>

            {/* Title scrolls normally on phone to avoid overlap with sticky controls */}
            {currentTitle && (
              <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                {currentTitle}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => calendarApi()?.prev()}
                  className="hc-cal-btn px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-200"
                    aria-label={t('admin.previous')}
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => calendarApi()?.next()}
                  className="hc-cal-btn px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-200"
                    aria-label={t('admin.next')}
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={() => calendarApi()?.today()}
                  className="hc-cal-btn px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-200"
                >
                    {t('admin.today')}
                </button>
              </div>

              <div className="text-sm md:text-base font-semibold text-zinc-900 dark:text-zinc-100 truncate text-right">
                {currentTitle}
              </div>
            </div>

            <div className="hc-cal-seg flex gap-2 overflow-x-auto -mx-1 px-1">
              <button
                type="button"
                onClick={() => calendarApi()?.changeView('dayGridMonth')}
                className={`hc-cal-seg-btn px-3 py-2 text-sm rounded-xl border ${currentView === 'dayGridMonth' ? 'bg-[--accent] border-[--accent] text-white' : 'border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 text-zinc-700 dark:text-zinc-200'} backdrop-blur-xl transition`}
                style={{ '--accent': BRAND_GOLD }}
              >
                {t('admin.month')}
              </button>
              <button
                type="button"
                onClick={() => calendarApi()?.changeView(getWeekView())}
                className={`hc-cal-seg-btn px-3 py-2 text-sm rounded-xl border ${currentView === getWeekView() ? 'bg-[--accent] border-[--accent] text-white' : 'border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 text-zinc-700 dark:text-zinc-200'} backdrop-blur-xl transition`}
                style={{ '--accent': BRAND_GOLD }}
              >
                {t('admin.week')}
              </button>
              <button
                type="button"
                onClick={() => calendarApi()?.changeView(getDayView())}
                className={`hc-cal-seg-btn px-3 py-2 text-sm rounded-xl border ${currentView === getDayView() ? 'bg-[--accent] border-[--accent] text-white' : 'border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 text-zinc-700 dark:text-zinc-200'} backdrop-blur-xl transition`}
                style={{ '--accent': BRAND_GOLD }}
              >
                {t('admin.day')}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="mb-4 grid grid-cols-2 sm:flex sm:flex-row sm:items-center gap-2 sm:gap-3 hc-filter-grid">
        {!isBarber && (
          <div className="flex items-center gap-1 sm:gap-2 col-span-1 min-w-0">
            <label className="hidden sm:block text-sm text-zinc-600 dark:text-zinc-300">{t('admin.col_barber')}</label>
            <select
              value={filters.barber_id}
              onChange={(e) => handleFilterChange('barber_id', e.target.value)}
              aria-label={t('admin.col_barber')}
              className="w-full min-w-0 px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-900 dark:text-zinc-100"
            >
              <option value="">{t('admin.all')}</option>
              {barbers.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1 sm:gap-2 col-span-1 min-w-0">
          <label className="hidden sm:block text-sm text-zinc-600 dark:text-zinc-300">{t('admin.col_status')}</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            aria-label={t('admin.col_status')}
            className="w-full min-w-0 px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-900 dark:text-zinc-100"
          >
            <option value="">{t('admin.all')}</option>
            <option value="pending">{t('admin.status_pending')}</option>
            <option value="confirmed">{t('admin.status_confirmed')}</option>
            <option value="completed">{t('admin.status_completed')}</option>
            <option value="no_show">{t('admin.status_no_show')}</option>
            <option value="cancelled">{t('admin.status_cancelled')}</option>
          </select>
        </div>
        <div className="col-span-2 sm:col-auto sm:ml-auto flex items-center gap-2 text-xs hc-legend flex-nowrap overflow-x-auto -mx-1 px-1">
          <span className="hc-pill"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: BRAND_GOLD }}></span><span>{t('admin.status_confirmed')}</span></span>
          <span className="hc-pill"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#F59E0B' }}></span><span>{t('admin.status_pending')}</span></span>
          <span className="hc-pill"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#10B981' }}></span><span>{t('admin.status_completed')}</span></span>
          <span className="hc-pill"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#EF4444' }}></span><span>{t('admin.status_no_show')}</span></span>
          <span className="hc-pill"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#6B7280' }}></span><span>{t('admin.status_cancelled')}</span></span>
        </div>
      </div>

      <div className="rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl p-2">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin].concat(listPluginModule ? [listPluginModule] : [])}
          headerToolbar={false}
          // Start in Month view on ALL screens (desktop + tablets + phones)
          initialView="dayGridMonth"
          height="auto"
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          slotLabelFormat={{ hour: 'numeric', minute: '2-digit', hour12: true }}
          eventTimeFormat={{ hour: 'numeric', minute: '2-digit', hour12: true }}
          // Month view should NOT show month/day in sticky headers (Sun, Mon, ... only).
          dayHeaderFormat={{ weekday: 'short' }}
          views={{
            timeGridWeek: { dayHeaderFormat: { weekday: 'short', month: 'short', day: 'numeric' } },
            timeGridDay: { dayHeaderFormat: { weekday: 'short', month: 'short', day: 'numeric' } },
          }}
          listDayFormat={{ weekday: 'short', month: 'short', day: 'numeric' }}
          listDaySideFormat={false}
          noEventsText={t('admin.no_bookings')}
          dayMaxEventRows={true}
          moreLinkClick="popover"
          expandRows
          stickyHeaderDates
          allDaySlot={true}
          nowIndicator
          weekends
          events={events}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          datesSet={onDatesSet}
          displayEventTime
          dayMaxEvents
          showNonCurrentDates={!isCompact}
          fixedWeekCount={!isCompact}
        />
      </div>
      {loading && (
        <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-2">{t('admin.loading_schedule')}</div>
      )}
    </div>
  );
}


